import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/api';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Extract session_id from multiple URL locations (hash, query, or full URL)
// Mobile browsers (Samsung Internet, Chrome on Android) may handle redirects differently
function extractSessionId() {
  // 1. Check hash fragment (standard flow)
  const hash = window.location.hash || '';
  const hashMatch = hash.match(/session_id=([^&]+)/);
  if (hashMatch && hashMatch[1]) return hashMatch[1];

  // 2. Check query params (some mobile browsers convert hash to query)
  const params = new URLSearchParams(window.location.search);
  const qsId = params.get('session_id');
  if (qsId) return qsId;

  // 3. Check full URL as last resort (catches edge cases)
  const fullMatch = window.location.href.match(/session_id=([^&#]+)/);
  if (fullMatch && fullMatch[1]) return fullMatch[1];

  return null;
}

/**
 * Dual-method API call: tries fetch first (simpler CORS handling), falls back to XHR.
 * NO withCredentials anywhere — token is in the JSON response body.
 */
async function apiCall(method, url, body = null) {
  const options = {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'omit',
    mode: 'cors'
  };

  // Attempt 1: fetch (preferred on modern mobile browsers)
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const response = await fetch(url, options);
      let text;
      try {
        text = await response.clone().text();
      } catch (bodyErr) {
        // "body stream already read" — retry with fresh fetch
        if (attempt < 3) { await new Promise(r => setTimeout(r, 1500 * attempt)); continue; }
        break; // Fall through to XHR
      }
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        if (attempt < 3) { await new Promise(r => setTimeout(r, 1500 * attempt)); continue; }
        throw new Error(`Server returned non-JSON response (HTTP ${response.status}). Please try again.`);
      }
      if (response.ok) return data;
      const errMsg = typeof data?.detail === 'string' ? data.detail
        : typeof data?.detail === 'object' ? (data.detail.error_description || data.detail.error || JSON.stringify(data.detail))
        : `Request failed (HTTP ${response.status})`;
      if (attempt < 3 && response.status >= 500) { await new Promise(r => setTimeout(r, 1500 * attempt)); continue; }
      throw new Error(errMsg);
    } catch (err) {
      if (err.message && !err.message.includes('fetch')) throw err; // Re-throw API errors
      if (attempt < 3) { await new Promise(r => setTimeout(r, 1500 * attempt)); continue; }
      // fetch itself failed (blocked, network error) — fall through to XHR
      break;
    }
  }

  // Attempt 2: XHR fallback (in case fetch is intercepted by service worker or platform)
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    if (body) xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.timeout = 20000;
    xhr.onload = function () {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) { resolve(data); return; }
        const errMsg = typeof data?.detail === 'string' ? data.detail
          : typeof data?.detail === 'object' ? (data.detail.error_description || data.detail.error || JSON.stringify(data.detail))
          : `Request failed (HTTP ${xhr.status})`;
        reject(new Error(errMsg));
      } catch (e) {
        reject(new Error(`Server returned non-JSON response (HTTP ${xhr.status}). Please try again.`));
      }
    };
    xhr.onerror = function () { reject(new Error('Network error — unable to reach the server.')); };
    xhr.ontimeout = function () { reject(new Error('Request timed out. Please try again.')); };
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

const AuthCallback = () => {
  const [status, setStatus] = useState('processing');
  const [errorMsg, setErrorMsg] = useState('');
  const hasRun = useRef(false);
  const { setToken, setUser, setLoading } = useAuth();

  // Version tag for debugging cache issues — if user sees "Invalid response from server"
  // instead of this component, they are on stale cached code
  const AUTH_VERSION = '2026.03.21';

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const doAuth = async () => {
      try {
        const sessionId = extractSessionId();
        if (!sessionId) {
          throw new Error('No session ID found in callback URL. Please try signing in again.');
        }

        // Step 1: Exchange session_id for Google user data via backend proxy
        const sessionData = await apiCall(
          'GET',
          `${API}/auth/google/session-data?session_id=${encodeURIComponent(sessionId)}`
        );

        if (!sessionData.email || !sessionData.id) {
          throw new Error('Incomplete data received from Google. Please try signing in again.');
        }

        // Step 2: Create or login user in our backend
        const rememberMe = localStorage.getItem('remember_me') !== 'false';
        const authData = await apiCall(
          'POST',
          `${API}/auth/google?remember_me=${rememberMe}`,
          {
            email: sessionData.email,
            google_id: sessionData.id,
            name: sessionData.name || '',
            picture: sessionData.picture || null
          }
        );

        if (!authData.token) {
          throw new Error('Login failed — no token received. Please try again.');
        }

        // Step 3: Store auth state
        localStorage.setItem('token', authData.token);
        try {
          localStorage.removeItem('oauth_pending');
          if (rememberMe) localStorage.setItem('remember_me', 'true');
          else localStorage.removeItem('remember_me');
        } catch (e) { /* localStorage might be full/blocked in private mode */ }
        setToken(authData.token);
        setUser(authData.user);
        setLoading(false);

        // Step 4: Clear the session_id from URL without reloading
        window.history.replaceState(null, '', window.location.pathname + window.location.search);

      } catch (err) {
        console.error('Google Auth error:', err);
        setErrorMsg(err.message || 'Authentication failed. Please try again.');
        setStatus('error');
      }
    };

    doAuth();
  }, [setToken, setUser, setLoading]);

  // Auto-redirect to login after showing error
  useEffect(() => {
    if (status === 'error') {
      const timer = setTimeout(() => {
        window.location.href = window.location.origin + window.location.pathname;
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  if (status === 'error') {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#ef4444', marginBottom: 15, fontSize: '1.2rem' }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }} />
            Sign In Failed
          </h2>
          <p style={{ color: '#a1a1aa', marginBottom: 10, fontSize: '0.9rem' }}>{errorMsg}</p>
          <p style={{ color: '#71717a', fontSize: '0.8rem' }}>Redirecting to login in 5 seconds...</p>
          <button onClick={() => { window.location.href = window.location.origin + window.location.pathname; }}
            data-testid="auth-error-back-btn"
            style={{ marginTop: 15, background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#0F172A', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
            Back to Login
          </button>
          <p style={{ color: '#52525b', fontSize: '0.7rem', marginTop: 12 }}>
            If this keeps happening, clear your browser cache/site data and try again.
          </p>
          <p style={{ color: '#3f3f46', fontSize: '0.65rem', marginTop: 4 }}>Auth v{AUTH_VERSION}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ textAlign: 'center' }}>
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p style={{ marginTop: 20, color: '#a1a1aa' }}>Completing Google sign in...</p>
        <p style={{ color: '#3f3f46', fontSize: '0.65rem', marginTop: 8 }}>v{AUTH_VERSION}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
